import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './Login.css';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

interface LoginUser {
  id: string;
  email: string;
  role: string;
}

interface LoginProps {
  onLogin: (user: LoginUser) => void;
  forceResetMode?: boolean;
}

type AuthView = 'signin' | 'signup' | 'forgot' | 'reset' | 'confirmed' | 'magic';

// ---------------------------------------------------------------------------
// OAuth provider registry — add a new provider here, zero other changes needed
// Supabase provider keys: https://supabase.com/docs/guides/auth/social-login
// ---------------------------------------------------------------------------
const OAUTH_PROVIDERS = [
  {
    key: 'google' as const,
    label: 'Google',
    bg: '#fff',
    border: '#dadce0',
    color: '#3c4043',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
  },
  {
    key: 'azure' as const,
    label: 'Microsoft',
    bg: '#fff',
    border: '#dadce0',
    color: '#3c4043',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.4 0H0v11.4h11.4V0z" fill="#F25022"/>
        <path d="M24 0H12.6v11.4H24V0z" fill="#7FBA00"/>
        <path d="M11.4 12.6H0V24h11.4V12.6z" fill="#00A4EF"/>
        <path d="M24 12.6H12.6V24H24V12.6z" fill="#FFB900"/>
      </svg>
    ),
  },
  {
    key: 'linkedin_oidc' as const,
    label: 'LinkedIn',
    bg: '#0A66C2',
    border: '#0A66C2',
    color: '#fff',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
  {
    key: 'github' as const,
    label: 'GitHub',
    bg: '#24292e',
    border: '#24292e',
    color: '#fff',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
      </svg>
    ),
  },
  {
    key: 'twitter' as const,
    label: 'X / Twitter',
    bg: '#000',
    border: '#000',
    color: '#fff',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
];

type OAuthProviderKey = typeof OAUTH_PROVIDERS[number]['key'];

const ROLES = [
  { value: 'sales_professional', label: 'Sales Professional' },
  { value: 'training_administrator', label: 'Training Administrator' },
  { value: 'system_administrator', label: 'System Administrator' },
];

const Login: React.FC<LoginProps> = ({ onLogin, forceResetMode = false }) => {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<AuthView>('signin');
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<OAuthProviderKey | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'sales_professional',
  });

  // Handle URL-driven mode: /login?mode=reset (from AuthCallback) or ?mode=confirmed
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'reset' || forceResetMode) {
      setView('reset');
    } else if (mode === 'confirmed') {
      setView('confirmed');
    }
  }, [searchParams, forceResetMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setSuccess('');
  };

  const switchTab = (tab: 'signin' | 'signup') => {
    setActiveTab(tab);
    setView(tab);
    setError('');
    setSuccess('');
    setFormData({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '', role: 'sales_professional' });
  };

  // ---------------------------------------------------------------------------
  // OAuth — works for any provider in OAUTH_PROVIDERS with zero additional code
  // ---------------------------------------------------------------------------
  const handleOAuth = async (providerKey: OAuthProviderKey) => {
    setError('');
    setOauthLoading(providerKey);
    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: providerKey as any,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (oauthError) throw oauthError;
      // Browser redirects — no further action here
    } catch (err: any) {
      setError(err.message || 'OAuth sign-in failed. Please try again.');
      setOauthLoading(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Email / password sign-in
  // ---------------------------------------------------------------------------
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.email.trim() || !formData.password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim(),
        password: formData.password,
      });
      if (signInError) {
        if (signInError.message.includes('Email not confirmed')) {
          setError('Email not confirmed. Check your inbox or contact your administrator.');
        } else if (signInError.message.includes('Invalid login credentials')) {
          setError('Incorrect email or password.');
        } else {
          setError(signInError.message);
        }
        return;
      }
      if (!data.user) throw new Error('Sign in failed. Please try again.');

      const { data: participant } = await supabase
        .from('participants')
        .select('role')
        .eq('user_id', data.user.id)
        .maybeSingle();

      onLogin({
        id: data.user.id,
        email: data.user.email || '',
        role: participant?.role || data.user.user_metadata?.role || 'participant',
      });
    } catch (err: any) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Sign up
  // ---------------------------------------------------------------------------
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const dbRole = formData.role === 'system_administrator' ? 'admin'
                   : formData.role === 'training_administrator' ? 'trainer'
                   : 'participant';

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email.trim(),
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: { first_name: formData.firstName.trim(), last_name: formData.lastName.trim(), role: dbRole },
        },
      });
      if (signUpError) throw signUpError;
      if (!data.user) throw new Error('No user returned from sign up.');

      await supabase.from('participants').upsert({
        user_id: data.user.id,
        email: data.user.email,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        role: dbRole,
      }, { onConflict: 'user_id' });

      if (data.session) {
        onLogin({ id: data.user.id, email: data.user.email || '', role: dbRole });
        return;
      }

      setSuccess('Account created! Check your email to confirm your address, then sign in.');
      switchTab('signin');
    } catch (err: any) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Forgot password
  // ---------------------------------------------------------------------------
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.email.trim()) { setError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        formData.email.trim(),
        { redirectTo: `${window.location.origin}/auth/callback` }
      );
      if (resetError) throw resetError;
      setSuccess('Password reset email sent. Check your inbox (and spam folder).');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Magic link sign-in
  // ---------------------------------------------------------------------------
  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) { setError('Please enter your email.'); return; }
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: formData.email.trim(),
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` }
      });
      if (error) throw error;
      setSuccess('Check your email — we sent you a sign-in link.');
    } catch (err: any) {
      setError(err.message || 'Failed to send link.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Password reset (arrived via AuthCallback after clicking email link)
  // ---------------------------------------------------------------------------
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (formData.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: formData.password });
      if (updateError) throw updateError;
      setSuccess('Password updated successfully!');
      setTimeout(() => {
        window.history.replaceState(null, '', window.location.pathname);
        switchTab('signin');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showTabs = view === 'signin' || view === 'signup';

  // Shared OAuth provider grid
  const OAuthGrid = () => (
    <>
      <div className="oauth-grid">
        {OAUTH_PROVIDERS.map(p => (
          <button
            key={p.key}
            className="oauth-btn"
            style={{ background: p.bg, borderColor: p.border, color: p.color }}
            onClick={() => handleOAuth(p.key)}
            disabled={oauthLoading !== null}
            title={`Continue with ${p.label}`}
          >
            {oauthLoading === p.key
              ? <span className="btn-spinner" style={{ borderColor: `${p.color}55`, borderTopColor: p.color }} />
              : p.icon}
            <span className="oauth-label">{p.label}</span>
          </button>
        ))}
      </div>
      <div className="divider">
        <span>{view === 'signup' ? 'or create account with email' : 'or sign in with email'}</span>
      </div>
    </>
  );

  return (
    <div className="login-container">
      <div className="login-card">

        <div className="brand-header">
          <div className="brand-logo">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="24" fill="url(#brandGrad)" />
              <path d="M14 24 Q24 12 34 24 Q24 36 14 24Z" fill="white" fillOpacity="0.9" />
              <circle cx="24" cy="24" r="5" fill="white" />
              <defs>
                <linearGradient id="brandGrad" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#009999" /><stop offset="1" stopColor="#007777" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <h1 className="brand-title">Training Portal</h1>
          <p className="brand-subtitle">Practice Platform — Powered by LiquidSMARTS™</p>
        </div>

        {showTabs && (
          <div className="tab-bar" role="tablist">
            <button role="tab" aria-selected={activeTab === 'signin'}
              className={`tab-button ${activeTab === 'signin' ? 'tab-active' : ''}`}
              onClick={() => switchTab('signin')}>Sign In</button>
            <button role="tab" aria-selected={activeTab === 'signup'}
              className={`tab-button ${activeTab === 'signup' ? 'tab-active' : ''}`}
              onClick={() => switchTab('signup')}>Sign Up</button>
          </div>
        )}

        <div className="form-container">

          {/* ── SIGN IN ── */}
          {view === 'signin' && (
            <>
              <OAuthGrid />
              <form onSubmit={handleSignIn} noValidate>
                <div className="field-group">
                  <label className="field-label">Email</label>
                  <input className="form-input" type="email" name="email"
                    value={formData.email} onChange={handleChange}
                    placeholder="you@example.com" required autoComplete="email" />
                </div>
                <div className="field-group">
                  <div className="field-label-row">
                    <label className="field-label">Password</label>
                    <button type="button" className="link-button"
                      onClick={() => { setView('forgot'); setError(''); setSuccess(''); }}>
                      Forgot password?
                    </button>
                  </div>
                  <input className="form-input" type="password" name="password"
                    value={formData.password} onChange={handleChange}
                    placeholder="••••••••" required autoComplete="current-password" />
                </div>
                {error && <div className="message error-message">{error}</div>}
                {success && <div className="message success-message">{success}</div>}
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : 'Sign In'}
                </button>
              </form>
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button type="button" className="link-button" onClick={() => { setView('magic'); setError(''); }}>
                  Sign in with a magic link instead
                </button>
              </div>
            </>
          )}

          {/* ── SIGN UP ── */}
          {view === 'signup' && (
            <>
              <OAuthGrid />
              <form onSubmit={handleSignUp} noValidate>
                <div className="field-row">
                  <div className="field-group">
                    <label className="field-label">First Name</label>
                    <input className="form-input" type="text" name="firstName"
                      value={formData.firstName} onChange={handleChange}
                      placeholder="Alex" required autoComplete="given-name" />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Last Name</label>
                    <input className="form-input" type="text" name="lastName"
                      value={formData.lastName} onChange={handleChange}
                      placeholder="Dawson" required autoComplete="family-name" />
                  </div>
                </div>
                <div className="field-group">
                  <label className="field-label">Email</label>
                  <input className="form-input" type="email" name="email"
                    value={formData.email} onChange={handleChange}
                    placeholder="you@example.com" required autoComplete="email" />
                </div>
                <div className="field-group">
                  <label className="field-label">Role</label>
                  <select className="form-input" name="role" value={formData.role} onChange={handleChange}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="field-group">
                  <label className="field-label">Password</label>
                  <input className="form-input" type="password" name="password"
                    value={formData.password} onChange={handleChange}
                    placeholder="Min. 8 characters" required autoComplete="new-password" />
                </div>
                <div className="field-group">
                  <label className="field-label">Confirm Password</label>
                  <input className="form-input" type="password" name="confirmPassword"
                    value={formData.confirmPassword} onChange={handleChange}
                    placeholder="••••••••" required autoComplete="new-password" />
                </div>
                {error && <div className="message error-message">{error}</div>}
                {success && <div className="message success-message">{success}</div>}
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : 'Create Account'}
                </button>
              </form>
            </>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {view === 'forgot' && (
            <form onSubmit={handleForgotPassword} noValidate>
              <div className="back-link">
                <button type="button" className="link-button"
                  onClick={() => { setView('signin'); setError(''); setSuccess(''); }}>
                  ← Back to Sign In
                </button>
              </div>
              <h3 className="form-heading">Reset Your Password</h3>
              <p className="form-subtext">Enter your email and we'll send you a reset link.</p>
              <div className="field-group">
                <label className="field-label">Email</label>
                <input className="form-input" type="email" name="email"
                  value={formData.email} onChange={handleChange}
                  placeholder="you@example.com" required autoComplete="email" />
              </div>
              {error && <div className="message error-message">{error}</div>}
              {success && <div className="message success-message">{success}</div>}
              {!success && (
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : 'Send Reset Link'}
                </button>
              )}
            </form>
          )}

          {/* ── PASSWORD RESET ── */}
          {view === 'reset' && (
            <form onSubmit={handlePasswordReset} noValidate>
              <h3 className="form-heading">Set New Password</h3>
              <p className="form-subtext">Choose a strong password for your account.</p>
              <div className="field-group">
                <label className="field-label">New Password</label>
                <input className="form-input" type="password" name="password"
                  value={formData.password} onChange={handleChange}
                  placeholder="Min. 8 characters" required autoComplete="new-password" />
              </div>
              <div className="field-group">
                <label className="field-label">Confirm Password</label>
                <input className="form-input" type="password" name="confirmPassword"
                  value={formData.confirmPassword} onChange={handleChange}
                  placeholder="••••••••" required autoComplete="new-password" />
              </div>
              {error && <div className="message error-message">{error}</div>}
              {success && <div className="message success-message">{success}</div>}
              {!success && (
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : 'Update Password'}
                </button>
              )}
            </form>
          )}

          {/* ── MAGIC LINK ── */}
          {view === 'magic' && (
            <form onSubmit={handleMagicLink} noValidate>
              <div className="back-link">
                <button type="button" className="link-button" onClick={() => { setView('signin'); setError(''); setSuccess(''); }}>
                  ← Back to Sign In
                </button>
              </div>
              <h3 className="form-heading">Email Sign-In Link</h3>
              <p className="form-subtext">Enter your email and we'll send you a one-click sign-in link. No password needed.</p>
              <div className="field-group">
                <label className="field-label">Email</label>
                <input className="form-input" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" required autoComplete="email" />
              </div>
              {error && <div className="message error-message">{error}</div>}
              {success && <div className="message success-message">{success}</div>}
              {!success && (
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? <span className="btn-spinner" /> : 'Send Sign-In Link'}
                </button>
              )}
            </form>
          )}

          {/* ── EMAIL CONFIRMED ── */}
          {view === 'confirmed' && (
            <div className="info-card">
              <div className="info-icon">✅</div>
              <h3>Email Confirmed!</h3>
              <p>Your email address has been verified. You can now sign in.</p>
              <button type="button" className="submit-button" style={{ marginTop: '20px' }}
                onClick={() => { window.history.replaceState(null, '', window.location.pathname); switchTab('signin'); }}>
                Sign In Now
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;
